import { PropsWithChildren, ReactNode } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { AtlassianLogo } from '@atlaskit/logo';
import { media } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

export type ModalSize = 'sm' | 'md' | 'lg';

export const Modal = ({
	children,
	showBrandFooter,
	showProductLogoOnLargeScreens,
	size,
	productLogo,
	moveLogoToModal,
}: PropsWithChildren<{
	showProductLogoOnLargeScreens?: boolean;
	size?: ModalSize;
	showBrandFooter?: boolean;
	productLogo?: ReactNode;
	moveLogoToModal?: boolean;
	// eslint-disable-next-line complexity
}>) => {
	const { formatMessage } = useIntl();
	const logoLabel = formatMessage({
		defaultMessage: 'Atlassian logo',
		id: 'signup.layouts.modal.atlassianLogoLabel',
	});

	return (
		<div
			css={[
				positioningCss,
				!moveLogoToModal && positioningCssWithGap,
				showProductLogoOnLargeScreens && noPositioningTopPaddingCss,
			]}
			data-testid="modal"
		>
			{productLogo && moveLogoToModal && (
				<div css={[logoWrapper, !showProductLogoOnLargeScreens && hideAboveSmCss]}>
					{productLogo}
				</div>
			)}
			<div
				css={[
					modalCss,
					size === 'sm' && smModalCss,
					size === 'md' && mdModalCss,
					size === 'lg' && lgModalCss,
				]}
			>
				{children}
			</div>
			{showBrandFooter && (
				<div css={[moveLogoToModal && footerCss]}>
					<div css={hideAboveSmCss} aria-hidden="true">
						<AtlassianLogo
							appearance="brand"
							size="small"
							testId="atlassian-logo-sm-screen"
							label={logoLabel}
						/>
					</div>
					<div css={lgVisibleCss} aria-hidden="true">
						<AtlassianLogo
							appearance="inverse"
							size="small"
							testId="atlassian-logo-lg-screen"
							label={logoLabel}
						/>
					</div>
				</div>
			)}
		</div>
	);
};

const hideAboveSmCss = css({
	[media.above.sm]: {
		display: 'none !important',
	},
});

const lgVisibleCss = css({
	display: 'none',
	[media.above.sm]: {
		display: 'block !important',
	},
});

const modalCss = css({
	overflow: 'hidden',
	transition: 'width 0.5s ease-in-out',
	maxWidth: '100%',
	[media.above.sm]: {
		backgroundColor: token('elevation.surface'),
		borderRadius: 12, // todo replace with radius.xlarge token when available
		boxShadow: token('elevation.shadow.overlay'),
		minHeight: 400,
	},
});

const smModalCss = css({
	width: 560,
});

const mdModalCss = css({
	width: 972,
});

const lgModalCss = css({
	width: 1140,
});

const positioningCss = css({
	alignItems: 'center',
	display: 'flex',
	flexDirection: 'column',
	padding: 24,
	[media.above.sm]: {
		justifyContent: 'flex-start',
		minHeight: '100vh',
		padding: token('space.1000'),
		['@supports (min-height: 100dvh)']: {
			minHeight: '100dvh',
		},
	},
});

const footerCss = css({
	marginBlockStart: token('space.400'),
});

const positioningCssWithGap = css({
	gap: token('space.400'),
});

const logoWrapper = css({
	display: 'flex',
	justifyContent: 'center',
	margin: `${token('space.100')} ${token('space.0')} ${token('space.200')}`,
	[media.above.sm]: {
		color: 'white',
		marginBlockStart: token('space.0'),
	},
});

const noPositioningTopPaddingCss = css({
	paddingBlockStart: token('space.300'),
	[media.above.sm]: {
		paddingBlockStart: token('space.500'),
	},
});
